import apiService from './api';
import secureStorage from '../utils/secureStorage';
import { sanitizeInput } from '../utils/security';

class AuthService {
  constructor() {
    this.token = secureStorage.getItem('auth_token');
    this.refreshToken = secureStorage.getItem('refresh_token');
    this.user = secureStorage.getItem('user');
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.token && !!this.user;
  }

  // Get current user
  getCurrentUser() {
    return this.user;
  }

  // Login user
  async login(credentials) {
    try {
      // Sanitize input credentials
      const sanitizedCredentials = {
        email: sanitizeInput(credentials.email),
        password: credentials.password // Don't sanitize password as it may contain special chars
      };

      const response = await apiService.login(sanitizedCredentials);

      if (response.token && response.user) {
        this.token = response.token;
        this.user = response.user;

        // Store in secure storage with longer expiry for auth token
        secureStorage.setItem('auth_token', this.token, 24 * 60 * 60 * 1000); // 24 hours
        secureStorage.setItem('user', this.user, 24 * 60 * 60 * 1000); // 24 hours

        // Set token in API service
        apiService.setToken(this.token);

        return response;
      }

      throw new Error(response.message || 'Login failed');
    } catch (error) {
      throw error;
    }
  }

  // Logout user
  async logout() {
    try {
      if (this.token) {
        await apiService.logout();
      }
    } catch (error) {
      // Handle error silently
    } finally {
      this.clearAuthData();
    }
  }

  // Clear authentication data
  clearAuthData() {
    this.token = null;
    this.user = null;

    secureStorage.removeItem('auth_token');
    secureStorage.removeItem('user');

    apiService.removeToken();
  }

  // Refresh authentication token
  async refreshAuthToken() {
    try {
      // Try to refresh with current token
      const response = await apiService.refreshToken();

      if (response && (response.success || response.token)) {
        this.token = response.token || response.data?.token;
        if (this.token) {
          secureStorage.setItem('auth_token', this.token, 24 * 60 * 60 * 1000); // 24 hours
          apiService.setToken(this.token);
          return response;
        }
      }

      throw new Error('Token refresh failed');
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearAuthData();
      throw error;
    }
  }

  // Get user profile
  async getProfile() {
    try {
      const response = await apiService.getProfile();

      if (response.success) {
        this.user = response.data;
        secureStorage.setItem('user', this.user, 24 * 60 * 60 * 1000); // 24 hours
        return response;
      }

      throw new Error('Failed to get profile');
    } catch (error) {
      throw error;
    }
  }

  // Check user permissions
  hasPermission(permission) {
    if (!this.user) return false;

    const rolePermissions = {
      super_admin: ['*'], // All permissions
      director: [
        'view_dashboard',
        'manage_users',
        'manage_teams',
        'manage_countries',
        'manage_universities',
        'manage_courses',
        'manage_content',
        'view_reports',
        'manage_admissions',
        'manage_enquiries'
      ],
      accounts_manager: [
        'view_dashboard',
        'manage_admissions',
        'manage_enquiries',
        'view_reports',
        'manage_documents'
      ],
      AccountsManager: [
        'view_dashboard',
        'manage_admissions',
        'manage_enquiries',
        'view_reports',
        'manage_documents'
      ],
      documentation_manager: [
        'view_dashboard',
        'manage_documents',
        'verify_documents',
        'view_reports'
      ],
      DocumentsManager: [
        'view_dashboard',
        'manage_documents',
        'verify_documents',
        'view_reports'
      ],
      team_lead: [
        'view_dashboard',
        'manage_referral_partners',
        'manage_admissions',
        'manage_tasks',
        'view_reports'
      ],
      seo_manager: [
        'view_dashboard',
        'manage_seo',
        'manage_blogs',
        'manage_content',
        'view_reports'
      ],
      content_manager: [
        'view_dashboard',
        'manage_blogs',
        'manage_news',
        'manage_gallery',
        'manage_features',
        'manage_testimonials',
        'manage_content'
      ],
      referral_partner: [
        'view_dashboard',
        'manage_own_admissions',
        'view_own_data'
      ]
    };

    const userPermissions = rolePermissions[this.user.role] || [];
    return userPermissions.includes('*') || userPermissions.includes(permission);
  }

  // Check if user has specific role
  hasRole(role) {
    return this.user?.role === role;
  }

  // Get user role
  getUserRole() {
    return this.user?.role || null;
  }

  // Auto-refresh token before expiry
  startTokenRefreshTimer() {
    if (!this.token) return;

    // Decode token to get expiry time (simplified)
    try {
      const tokenData = JSON.parse(atob(this.token.split('.')[1]));
      const expiryTime = tokenData.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;

      // Refresh token 5 minutes before expiry
      const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 0);

      setTimeout(() => {
        this.refreshAuthToken().catch(() => {
          // If refresh fails, logout user
          this.logout();
        });
      }, refreshTime);
    } catch (error) {
      // Handle error silently
    }
  }

  // Initialize auth service
  init() {
    if (this.isAuthenticated()) {
      apiService.setToken(this.token);
      this.startTokenRefreshTimer();
    }
  }
}

const authService = new AuthService();
export default authService;